import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { 
  getFirestore, 
  connectFirestoreEmulator, 
  collection, 
  getDocs, 
  query, 
  orderBy, 
  limit, 
  where,
  initializeFirestore,
  deleteDoc,
  doc,
  Timestamp
} from 'firebase/firestore';
import { initializeApp, getApps } from 'firebase/app';
import { db } from '../../firebase';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Alert, AlertTitle, AlertDescription } from '../../components/ui/alert';
import { LoadingSpinner } from '../../components/ui/loading-spinner';

const ReminderDashboard = () => {
  // State for environment switching
  const [useEmulator, setUseEmulator] = useState(false);
  const [emulatorDb, setEmulatorDb] = useState(null);
  const [currentDb, setCurrentDb] = useState(db);
  const [functions, setFunctions] = useState(() => getFunctions());
  
  // State for data
  const [reminderCounts, setReminderCounts] = useState(null);
  const [loadingCounts, setLoadingCounts] = useState(false);
  const [recentLogs, setRecentLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // State for manual sending
  const [sendingReminders, setSendingReminders] = useState(false);
  const [lastExecutionLog, setLastExecutionLog] = useState(null);

  // NEW: Data management state
  const [purgingLogs, setPurgingLogs] = useState(false);
  const [logStats, setLogStats] = useState(null);

  // Inline message state
  const [errors, setErrors] = useState([]);
  const [successMessages, setSuccessMessages] = useState([]);

  // Utility functions
  const safeGetNumber = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
        const num = parseInt(value);
        return isNaN(num) ? 0 : num;
    }
    if (typeof value === 'object' && value !== null) {
        if (value.total !== undefined) {
            if (typeof value.total === 'number') return value.total;
            if (typeof value.total === 'string') {
                const numTotal = parseInt(value.total);
                return isNaN(numTotal) ? 0 : numTotal;
            }
            return 0;
        }
        if (value.sent !== undefined) {
             if (typeof value.sent === 'number') return value.sent;
             if (typeof value.sent === 'string') {
                const numSent = parseInt(value.sent);
                return isNaN(numSent) ? 0 : numSent;
            }
            return 0;
        }
    }
    return 0;
  };

  const safeRender = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const addError = (message) => {
    setErrors(prev => [...prev, { id: Date.now(), message }]);
    setTimeout(() => setErrors(prev => prev.slice(1)), 5000);
  };

  const addSuccess = (message) => {
    setSuccessMessages(prev => [...prev, { id: Date.now(), message }]);
    setTimeout(() => setSuccessMessages(prev => prev.slice(1)), 5000);
  };

  // Initialize emulator connection
  useEffect(() => {
    if (window.location.hostname === 'localhost') {
      try {
        // Create a separate Firebase app instance for emulator
        const emulatorConfig = {
          apiKey: "demo-key",
          authDomain: "demo-project.firebaseapp.com",
          projectId: "demo-project",
          storageBucket: "demo-project.appspot.com",
          messagingSenderId: "123456789",
          appId: "demo-app-id"
        };
        
        // Check if emulator app already exists
        let emulatorApp;
        const existingEmulatorApp = getApps().find(app => app.name === 'emulator-app');
        
        if (existingEmulatorApp) {
          emulatorApp = existingEmulatorApp;
          console.log('Using existing emulator app');
        } else {
          emulatorApp = initializeApp(emulatorConfig, 'emulator-app');
          console.log('Created new emulator app');
        }
        
        // Initialize Firestore for emulator app
        const emulatorFirestore = initializeFirestore(emulatorApp, {
          host: 'localhost:8080',
          ssl: false
        });
        
        setEmulatorDb(emulatorFirestore);
        
        // Connect Functions emulator
        const functionsInstance = getFunctions();
        connectFunctionsEmulator(functionsInstance, 'localhost', 5001);
        setFunctions(functionsInstance);
        
        console.log('Connected to Firebase emulators');
      } catch (error) {
        console.warn('Emulator connection warning:', error);
        console.log('Will use production database only');
      }
    }
  }, []);

  // Update current database when toggle changes
  useEffect(() => {
    if (useEmulator && emulatorDb) {
      setCurrentDb(emulatorDb);
      console.log('Switched to emulator Firestore');
      addSuccess('🔧 Emulator Mode: Now using local emulator data.');
    } else {
      setCurrentDb(db);
      console.log('Switched to production Firestore');
      if (emulatorDb) { // Only show if switched, not on initial load
        addSuccess('🌐 Production Mode: Now using live production data.');
      }
    }
  }, [useEmulator, emulatorDb]);

  // Load data when database changes
  useEffect(() => {
    if (currentDb) {
      loadRecentLogs();
      loadReminderCounts();
      loadLogStats(); // NEW: Load log statistics
    }
  }, [currentDb]);

  // Load recent execution logs (SIMPLIFIED - only actual sends, not counts)
  const loadRecentLogs = async () => {
    try {
      setLoadingLogs(true);
      
      if (useEmulator) {
        // Show sample data for emulator
        const sampleLogs = generateSampleLogs();
        setRecentLogs(sampleLogs);
        addSuccess('Using Sample Data: Emulator mode active.');
      } else {
        // Only load actual reminder sends, not count operations
        const logsQuery = query(
          collection(currentDb, 'reminderJobLogs'),
          where('mode', '==', 'send-scheduled'), // UPDATED: Filter for 'send-scheduled'
          orderBy('timestamp', 'desc'),
          limit(20) // Show more actual sends
        );
        
        const querySnapshot = await getDocs(logsQuery);
        const logs = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setRecentLogs(logs);
        if (logs.length > 0) {
          addSuccess(`Loaded ${logs.length} recent reminder executions ('send-scheduled' operations).`);
        } else {
          addError("No 'send-scheduled' logs found. Check if reminders are running and logging with this mode.");
        }
      }
    } catch (error) {
      console.error('Error loading recent logs:', error);
      addError(`Error Loading Logs: ${safeRender(error.message)}`);
      setRecentLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  // Load reminder counts (MODIFIED to not create logs for counts)
  const loadReminderCounts = async () => {
    try {
      setLoadingCounts(true);
      setReminderCounts(null);
      
      console.log("Loading reminder counts (silent mode)...");
      
      const getReminderCounts = httpsCallable(functions, 'sendGdcInsuranceReminders');
      const result = await getReminderCounts({ 
        mode: 'count', 
        countOnly: true,
        silent: true // NEW: Tell backend not to log count operations
      });
      
      const countData = result.data;
      setReminderCounts(countData);
      
      const pendingCount = safeGetNumber(countData.totalPending);
      // Only show success message if there are pending reminders
      if (pendingCount > 0) {
        addSuccess(`${pendingCount} reminders are ready to send.`);
      }
    } catch (error) {
      console.error('Error loading reminder counts:', error);
      addError(`Error loading counts: ${safeRender(error.message)}`);
      setReminderCounts(null);
    } finally {
      setLoadingCounts(false);
    }
  };

  // NEW: Load log statistics
  const loadLogStats = async () => {
    try {
      const logsRef = collection(currentDb, 'reminderJobLogs');
      
      // Get total count
      const allLogsQuery = query(logsRef);
      const allLogsSnapshot = await getDocs(allLogsQuery);
      
      // Get count logs (the problematic ones)
      const countLogsQuery = query(logsRef, where('mode', '==', 'count'));
      const countLogsSnapshot = await getDocs(countLogsQuery);

      // Get FORCED_COUNT_ONLY logs
      const forcedCountLogsQuery = query(logsRef, where('mode', '==', 'FORCED_COUNT_ONLY'));
      const forcedCountLogsSnapshot = await getDocs(forcedCountLogsQuery);
      
      // Get old logs (older than 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const oldLogsQuery = query(logsRef, where('timestamp', '<', Timestamp.fromDate(thirtyDaysAgo)));
      const oldLogsSnapshot = await getDocs(oldLogsQuery);
      
      setLogStats({
        total: allLogsSnapshot.size,
        countOperations: countLogsSnapshot.size,
        forcedCountOnlyOperations: forcedCountLogsSnapshot.size, // NEW STAT
        oldLogs: oldLogsSnapshot.size,
        sendOperations: allLogsSnapshot.size - countLogsSnapshot.size - forcedCountLogsSnapshot.size // Adjust sendOperations
      });
    } catch (error) {
      console.error('Error loading log stats:', error);
      setLogStats(null);
    }
  };

  // NEW: Purge old logs
  const purgeOldLogs = async () => {
    try {
      setPurgingLogs(true);
      
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const oldLogsQuery = query(
        collection(currentDb, 'reminderJobLogs'),
        where('timestamp', '<', Timestamp.fromDate(thirtyDaysAgo))
      );
      
      const oldLogsSnapshot = await getDocs(oldLogsQuery);
      const deletePromises = oldLogsSnapshot.docs.map(docRef => deleteDoc(docRef.ref));
      
      await Promise.all(deletePromises);
      
      addSuccess(`Purged ${oldLogsSnapshot.size} old log entries (older than 30 days).`);
      
      // Refresh data
      loadRecentLogs();
      loadLogStats();
      
    } catch (error) {
      console.error('Error purging logs:', error);
      addError(`Error purging logs: ${safeRender(error.message)}`);
    } finally {
      setPurgingLogs(false);
    }
  };

  // NEW: Purge count operation logs specifically
  const purgeCountLogs = async () => {
    try {
      setPurgingLogs(true);
      
      const countLogsQuery = query(
        collection(currentDb, 'reminderJobLogs'),
        where('mode', '==', 'count') // This targets general 'count' logs
      );
      
      const countLogsSnapshot = await getDocs(countLogsQuery);
      const deletePromises = countLogsSnapshot.docs.map(docRef => deleteDoc(docRef.ref));
      
      await Promise.all(deletePromises);
      
      addSuccess(`Purged ${countLogsSnapshot.size} count operation logs.`);
      
      // Refresh data
      loadRecentLogs();
      loadLogStats();
      
    } catch (error) {
      console.error('Error purging count logs:', error);
      addError(`Error purging count logs: ${safeRender(error.message)}`);
    } finally {
      setPurgingLogs(false);
    }
  };

  // NEW: Purge FORCED_COUNT_ONLY logs specifically
  const purgeForcedCountOnlyLogs = async () => {
    try {
      setPurgingLogs(true);
      
      const forcedCountLogsQuery = query(
        collection(currentDb, 'reminderJobLogs'),
        where('mode', '==', 'FORCED_COUNT_ONLY')
      );
      
      const forcedCountLogsSnapshot = await getDocs(forcedCountLogsQuery);
      if (forcedCountLogsSnapshot.empty) {
        addSuccess('No "FORCED_COUNT_ONLY" logs found to purge.');
        setPurgingLogs(false);
        return;
      }
      const deletePromises = forcedCountLogsSnapshot.docs.map(docRef => deleteDoc(docRef.ref));
      
      await Promise.all(deletePromises);
      
      addSuccess(`Purged ${forcedCountLogsSnapshot.size} "FORCED_COUNT_ONLY" logs.`);
      
      // Refresh data
      loadRecentLogs();
      loadLogStats();
      
    } catch (error) {
      console.error('Error purging FORCED_COUNT_ONLY logs:', error);
      addError(`Error purging "FORCED_COUNT_ONLY" logs: ${safeRender(error.message)}`);
    } finally {
      setPurgingLogs(false);
    }
  };

  // Helper function to generate sample logs for development
  const generateSampleLogs = () => {
    const now = new Date();
    return [
      {
        id: 'sample-1',
        timestamp: now,
        success: true,
        scheduledBy: 'manual-admin',
        results: {
          gdcReminders: { sent: 2, failed: 0 },
          insuranceReminders: { sent: 3, failed: 0 },
          usersProcessed: 10
        },
        mode: 'send',
        executionTimeMs: 1243,
        isSampleData: true // Add flag to identify sample data
      },
      {
        id: 'sample-2',
        timestamp: new Date(now.getTime() - 86400000), // 1 day ago
        success: true,
        scheduledBy: 'cloud-scheduler',
        results: {
          gdcReminders: { sent: 1, failed: 0 },
          insuranceReminders: { sent: 0, failed: 0 },
          usersProcessed: 8
        },
        mode: 'send',
        executionTimeMs: 987,
        isSampleData: true // Add flag to identify sample data
      },
      {
        id: 'sample-3',
        timestamp: new Date(now.getTime() - 172800000), // 2 days ago
        success: false,
        scheduledBy: 'manual-admin',
        results: {
          gdcReminders: { sent: 0, failed: 1 },
          insuranceReminders: { sent: 0, failed: 0 },
          usersProcessed: 5
        },
        errors: ['User processing failed: Invalid email address'],
        mode: 'send',
        executionTimeMs: 567,
        isSampleData: true // Add flag to identify sample data
      }
    ].map(log => ({...log, isSampleData: true})); // Ensure all sample logs are flagged
  };

  // Simplified refresh function
  const handleRefresh = () => {
    try {
      loadRecentLogs();
      loadReminderCounts();
      loadLogStats();
      addSuccess('Dashboard refreshed.');
    } catch (error) {
      addError(`Refresh Error: ${safeRender(error.message)}`);
    }
  };

  // Simplified trigger reminders function
  const triggerReminders = async () => {
    try {
      setSendingReminders(true);
      setLastExecutionLog(null);
      
      const sendReminders = httpsCallable(functions, 'sendGdcInsuranceReminders');
      const requestData = {
        scheduledBy: 'manual-dashboard',
        mode: 'send-scheduled' // UPDATED: Ensure manual trigger also uses 'send-scheduled'
      };
      
      console.log('Triggering reminder function with mode "send-scheduled"...');
      const result = await sendReminders(requestData);
      
      setLastExecutionLog(result.data);
      
      const usersProcessed = safeGetNumber(result.data?.usersProcessed);
      const gdcSent = safeGetNumber(result.data?.gdcReminders);
      const insuranceSent = safeGetNumber(result.data?.insuranceReminders);
      
      addSuccess(`Reminders sent: ${gdcSent} GDC, ${insuranceSent} Insurance (${usersProcessed} users processed).`);
      
      // Refresh to show new execution
      setTimeout(() => {
        loadRecentLogs();
        loadLogStats();
      }, 2000);
      
    } catch (error) {
      console.error('Error triggering reminders:', error);
      addError(`Error sending reminders: ${safeRender(error.message)}`);
      setLastExecutionLog({ error: true, message: safeRender(error.message) });
    } finally {
      setSendingReminders(false);
    }
  };

  return (
    <div className="container mx-auto py-8 px-4">
      {/* Inline Error and Success Messages */}
      {errors.length > 0 && (
        <div className="mb-4 space-y-2">
          {errors.map(err => (
            <Alert key={err.id} className="bg-red-50 border-red-200">
              <AlertTitle className="text-red-800">Error</AlertTitle>
              <AlertDescription className="text-red-700">{safeRender(err.message)}</AlertDescription>
            </Alert>
          ))}
        </div>
      )}
      {successMessages.length > 0 && (
        <div className="mb-4 space-y-2">
          {successMessages.map(msg => (
            <Alert key={msg.id} className="bg-green-50 border-green-200">
              <AlertTitle className="text-green-800">Success</AlertTitle>
              <AlertDescription className="text-green-700">{safeRender(msg.message)}</AlertDescription>
            </Alert>
          ))}
        </div>
      )}

      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">Reminder Dashboard</h1>
        
        <div className="flex items-center gap-4">
          {window.location.hostname === 'localhost' && (
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setUseEmulator(!useEmulator)}
                variant={useEmulator ? "default" : "outline"}
                size="sm"
                className={useEmulator ? "bg-orange-600 hover:bg-orange-700" : ""}
              >
                {useEmulator ? "🔧 Emulator" : "🌐 Production"}
              </Button>
            </div>
          )}
          
          <Button onClick={handleRefresh} variant="outline" size="sm">
            🔄 Refresh
          </Button>
          
          <Link 
            to="/admin/email" 
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Email Admin
          </Link>
        </div>
      </div>

      {/* Environment Indicator */}
      {window.location.hostname === 'localhost' && useEmulator && (
        <Alert className="mb-6 bg-orange-50 border-orange-200">
          <AlertTitle className="text-orange-800">🔧 Emulator Mode</AlertTitle>
          <AlertDescription className="text-orange-700">
            Using sample data for demonstration. Switch to Production mode to see real data.
          </AlertDescription>
        </Alert>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Pending Reminders</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingCounts ? (
              <LoadingSpinner size="sm" />
            ) : (
              <div className="text-3xl font-bold text-blue-600">
                {safeGetNumber(reminderCounts?.totalPending)}
              </div>
            )}
            <p className="text-sm text-gray-500">Ready to send</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">
              {recentLogs.filter(log => log.success).length}
            </div>
            <p className="text-sm text-gray-500">Successful sends</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Data Management</CardTitle>
          </CardHeader>
          <CardContent>
            {logStats ? (
              <div className="space-y-1">
                <div className="text-sm">
                  <span className="font-medium">{logStats.total}</span> total logs
                </div>
                <div className="text-sm text-orange-600">
                  <span className="font-medium">{logStats.countOperations}</span> 'count' operations
                </div>
                <div className="text-sm text-yellow-600"> {/* Style for forced count logs */}
                  <span className="font-medium">{logStats.forcedCountOnlyOperations}</span> 'FORCED_COUNT_ONLY' ops
                </div>
                <div className="text-sm text-gray-500">
                  <span className="font-medium">{logStats.oldLogs}</span> old entries
                </div>
              </div>
            ) : (
              <LoadingSpinner size="sm" />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Manual Controls */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Manual Controls</CardTitle>
          <CardDescription>Send reminders and manage data</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-4">
              <div>
                <h3 className="font-medium mb-2">Send Reminders</h3>
                <Button
                  onClick={triggerReminders}
                  disabled={sendingReminders}
                  className="w-full"
                >
                  {sendingReminders ? <LoadingSpinner size="sm" /> : 'Send Reminders Now'}
                </Button>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="font-medium mb-2">Data Management</h3>
                <div className="space-y-2">
                  <Button
                    onClick={purgeCountLogs}
                    disabled={purgingLogs || !logStats?.countOperations}
                    variant="outline"
                    className="w-full"
                  >
                    {purgingLogs ? <LoadingSpinner size="sm" /> : `Clean 'count' Logs (${logStats?.countOperations || 0})`}
                  </Button>
                  <Button
                    onClick={purgeForcedCountOnlyLogs} // NEW BUTTON
                    disabled={purgingLogs || !logStats?.forcedCountOnlyOperations}
                    variant="outline"
                    className="w-full border-yellow-500 hover:bg-yellow-100" // Distinct styling
                  >
                    {purgingLogs ? <LoadingSpinner size="sm" /> : `Clean 'FORCED_COUNT_ONLY' Logs (${logStats?.forcedCountOnlyOperations || 0})`}
                  </Button>
                  <Button
                    onClick={purgeOldLogs}
                    disabled={purgingLogs || !logStats?.oldLogs}
                    variant="outline"
                    className="w-full"
                  >
                    {purgingLogs ? <LoadingSpinner size="sm" /> : `Clean Old Logs (${logStats?.oldLogs || 0})`}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Last Execution Results */}
          {lastExecutionLog && (
            <div className="mt-6 p-4 border rounded bg-gray-50">
              <h4 className="font-medium mb-3">Last Execution Results</h4>
              {lastExecutionLog.error ? (
                <div className="text-red-600">
                  <p>Error: {safeRender(lastExecutionLog.message)}</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Status:</span>
                    <div className="font-medium">{lastExecutionLog.success ? 'Success' : 'Failed'}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">GDC Reminders:</span>
                    <div className="font-medium">{safeGetNumber(lastExecutionLog.gdcReminders)}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Insurance Reminders:</span>
                    <div className="font-medium">{safeGetNumber(lastExecutionLog.insuranceReminders)}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Users Processed:</span>
                    <div className="font-medium">{safeGetNumber(lastExecutionLog.usersProcessed)}</div>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Executions */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Reminder Executions</CardTitle>
          <CardDescription>
            {useEmulator 
              ? "Sample data - showing example reminder executions" 
              : "Last 20 'send-scheduled' operations (other modes filtered out)"} {/* UPDATED description */}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingLogs ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner size="lg" />
            </div>
          ) : recentLogs.length > 0 ? (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {recentLogs.map((log) => (
                <div 
                  key={log.id} 
                  // REMOVED light background classes like bg-green-50/30
                  className={`p-3 border rounded ${log.isSampleData ? "border-orange-400" : log.success ? "border-green-400" : "border-red-400"}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-3">
                      {/* Using lighter text colors for better contrast on dark theme */}
                      <span className={`text-sm font-medium ${log.isSampleData ? 'text-orange-300' : log.success ? 'text-green-300' : 'text-red-300'}`}>
                        {log.success ? '✅ Success' : '❌ Failed'}
                      </span>
                      {log.isSampleData && (
                        <span className="text-xs px-2 py-1 bg-orange-700 text-orange-100 rounded"> {/* Darker bg for sample tag, lighter text */}
                          Sample
                        </span>
                      )}
                    </div>
                    {/* Lighter text for timestamp */}
                    <span className={`text-xs ${log.isSampleData ? 'text-orange-400' : 'text-gray-400'}`}>
                      {log.timestamp?.toDate ? 
                        log.timestamp.toDate().toLocaleString() : 
                        log.timestamp instanceof Date ? 
                          log.timestamp.toLocaleString() : 'Unknown time'}
                    </span>
                  </div>
                  
                  {/* Lighter text for log details */}
                  <div className={`grid grid-cols-3 gap-4 text-sm ${log.isSampleData ? 'text-orange-300' : 'text-gray-300'}`}>
                    <div>
                      <span className={`${log.isSampleData ? 'text-orange-400' : 'text-gray-400'}`}>GDC:</span>
                      <span className="ml-1 font-medium">{safeGetNumber(log.results?.gdcReminders)} sent</span>
                    </div>
                    <div>
                      <span className={`${log.isSampleData ? 'text-orange-400' : 'text-gray-400'}`}>Insurance:</span>
                      <span className="ml-1 font-medium">{safeGetNumber(log.results?.insuranceReminders)} sent</span>
                    </div>
                    <div>
                      <span className={`${log.isSampleData ? 'text-orange-400' : 'text-gray-400'}`}>Users:</span>
                      <span className="ml-1 font-medium">{safeGetNumber(log.results?.usersProcessed)} processed</span>
                    </div>
                  </div>
                  
                  {/* Lighter text for triggered by */}
                  <div className={`mt-2 text-xs ${log.isSampleData ? 'text-orange-400' : 'text-gray-400'}`}>
                    Triggered by: {safeRender(log.scheduledBy) || 'unknown'}
                  </div>
                  
                  {log.errors && log.errors.length > 0 && (
                    <div className={`mt-2 text-xs ${log.isSampleData ? 'text-orange-300' : 'text-red-300'}`}>
                      {log.errors.length} error(s) occurred
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>No reminder executions found</p>
              <p className="text-xs mt-1">Executions will appear here after reminders are sent</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Help */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle>System Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 text-sm">
            <div>
              <h3 className="font-medium">Log Display:</h3>
              <p className="text-xs">The "Recent Reminder Executions" list below shows logs where `mode` is 'send-scheduled'. Other types of logs (e.g., 'count') are not displayed in this list but are included in "Data Management" statistics.</p> {/* UPDATED description */}
            </div>
            <div>
              <h3 className="font-medium">How it works:</h3>
              <ol className="list-decimal ml-5 mt-1 space-y-1">
                <li>Cloud Scheduler triggers the reminder function daily</li>
                <li>Function checks for users with GDC/Insurance expiring in 30, 14, 7, or 0 days</li>
                <li>Sends email reminders to users who have consented</li>
                <li>Logs execution results for monitoring</li>
              </ol>
            </div>
            
            <div>
              <h3 className="font-medium">Data Management:</h3>
              <ul className="list-disc ml-5 mt-1 space-y-1">
                <li><strong>Count Logs:</strong> Created when checking pending reminders (can accumulate quickly). These are logs with `mode: 'count'`.</li>
                <li><strong>FORCED_COUNT_ONLY Logs:</strong> Specific count logs generated during testing/debugging.</li>
                <li><strong>Send Logs:</strong> Created when actually sending reminders (important to keep).</li>
                <li><strong>Old Logs:</strong> Entries older than 30 days (safe to remove periodically)</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReminderDashboard;
